import * as React from 'react';

import { AffectedFamilyPageProps } from "./affected-family-page.types";
import { FieldSelectorOnChangeMultiple } from '@common/components/FieldSelector/field-selector.types';
import { I18n } from 'react-redux-i18n';
import { getUniqueFieldCombination } from '@common/utils/fields';
import { FieldType } from '@common/dto/field/type';
import { QuestionnaireSubfieldUpdateDto } from '@common/dto/questionnaire/field/subfield-update-dto';
import { QuestionnaireFieldUpdateDto } from '@common/dto/questionnaire/field/update-dto';
import { QuestionnaireFieldAddDto } from '@common/dto/questionnaire/field/add-dto';
import { QuestionnaireAffectedFamilyPageUpdateDto } from '@common/dto/questionnaire/page/affected-family/update-dto';
import { FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT } from '@modules/fhq-design-tool/constants/shared';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import { useTextBlock } from '../text-block/text-block.hook';
import { QuestionnaireTextBlockLocalDto } from '@modules/fhq-design-tool/dto/text-block-create-dto';
import { useFieldsWithTextBlock } from '../../../fields-with-text-blocks/fields-with-text-blocks.hooks';
import { UpdatedLocalTexts, UpdatedLocalFields } from '../../../fields-with-text-blocks/fields-with-text-blocks.types';
import { getFhq4QuestionnaireFieldData } from '@modules/fhq-design-tool/services/questionnaires';

export const useAffectedFamilyPageController = (props: AffectedFamilyPageProps) => {
  const { data, onChange, showErrorMessage, cachedData, getPage, getFhq4QuestionnaireField, questionnaire, errors, questionnaireId } = props;
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false);
  const [editingFieldUniqueCombination, setEditingFieldUniqueCombination] = React.useState<string | undefined>();
  const [loadingPage, setLoadingPage] = React.useState(false);

  const initializeData = async () => {
    setLoadingPage(true);

    if (!cachedData) {
      try {
        await getPage(data.id);
      } catch (err) {
        console.error(err);
        showErrorMessage(I18n.t('Failed to get page'))
      }
    }
    
    setLoadingPage(false)
  };

  React.useEffect(() => {
    initializeData();
  }, [data.id]);

  const updatePage = React.useCallback((dataToUpdate: Partial<QuestionnaireAffectedFamilyPageUpdateDto>) => {
    if (cachedData) {
      onChange({
        ...cachedData,
        ...dataToUpdate,
        modified: true,
      });
    }
  }, [cachedData, onChange]);

  const {
    editingTextBlock,
    addTextBlock,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
    handleTextBlockDelete,
    handleOrderChange,
  } = useTextBlock({
    data: cachedData as { texts?: Array<QuestionnaireTextBlockLocalDto> },
    onChange: updatePage,
  });

  const fields = cachedData?.fields || [];
  const [editingField, setEditingField] = React.useState<QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto | undefined>();

  const navigateOnFieldEdit = React.useCallback(
    async (field: FieldInList) => {
      const fieldId = field.original.id;
      try {
        if (questionnaireId && fieldId) {
          const fullField = await getFhq4QuestionnaireFieldData(
            questionnaireId,
            data.id,
            fieldId
          );
          setEditingFieldUniqueCombination(
            getUniqueFieldCombination({
              id: fullField.progenyId,
              context: fullField.context,
              type: fullField.progenyFieldType,
            })
          );

          setEditingField(fullField);
        }
      } catch (err) {
        console.error(err);
        showErrorMessage("Failed to load field data");
      }
    },
    [getFhq4QuestionnaireField, data]
  );

  const processedFields = React.useMemo(() => {
    return fields
      .map((x): FieldInList => ({
        id: getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }),
        name: x.name,
        original: x,
      }))
      .sort((a, b) => a.original.order - b.original.order);
  }, [fields]);

  const { getCombinedItems } = useFieldsWithTextBlock<FieldInList, QuestionnaireTextBlockLocalDto>({ fields: processedFields, texts: (cachedData?.texts || []) as Array<QuestionnaireTextBlockLocalDto> });
  const combinedItems = React.useMemo(getCombinedItems, [processedFields, cachedData?.texts]);

  const handlePageNameChange = React.useCallback((val: string) => {
    updatePage({
      title: val.trim()
    });
  }, [updatePage]);

  const handlePageDescriptionChange = React.useCallback((val: string) => {
    updatePage({
      description: val
    });
  }, [updatePage]);

  type FieldInList = {
    id: string;
    name: string;
    original: QuestionnaireFieldUpdateDto | QuestionnaireFieldAddDto;
  };

  const handleFieldsOrderChange = React.useCallback((newFieldsOrder: Array<FieldInList>) => {
    updatePage({
      fields: newFieldsOrder.map((x, i) => ({
        ...x.original,
        order: i + 1,
      }))
    });
  }, [updatePage]);

  const openFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(true);
  }, []);

  const closeFieldChooser = React.useCallback(() => {
    setFieldChooserOpened(false);
  }, []);

  const handleFieldAdd = React.useCallback(
    async ({ fields }: FieldSelectorOnChangeMultiple) => {
      const newFields: Array<QuestionnaireFieldAddDto | QuestionnaireFieldListDto> = [];

      await Promise.all(
        fields.map(async (field, fieldIndex) => {
          const hasSameField = cachedData?.fields.some(
            (x) =>
              getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) ===
              getUniqueFieldCombination({ id: field.fieldId, context: field.fieldContext, type: field.fieldTypeId })
          );

          if (hasSameField || !cachedData) {
            return;
          }

          try {
            const fullField = await getFhq4QuestionnaireField({
              id: field.fieldId,
              context: field.fieldContext,
              system: field.systemField,
            });
            
            const newField: QuestionnaireFieldAddDto | QuestionnaireFieldListDto = {
              progenyId: field.fieldId as number,
              name: field.fieldName,
              path: fullField.path || fullField.name,
              order: combinedItems.length + fieldIndex + 1,
              systemField: field.systemField,
              context: field.fieldContext,
              type: fullField.type,
              progenyFieldType: field.fieldTypeId as FieldType,
              description: fullField.description,
              required: fullField.required,
              readonly: fullField.readonly,
              defaultValue: fullField.defaultValue,
              allowDuplications: fullField.allowDuplications,
              skipLogic: fullField.skipLogic,
              options: fullField.options,
              subfields: fullField.subfields,
              skipLogicEnabled: fullField.skipLogicEnabled,
              displayInFHQ: fullField.displayInFHQ,
              possibleDropdownOptions: fullField.possibleDropdownOptions,
              modified: true
            };

            newFields.push(newField);
          } catch (err) {
            console.error(err);
            showErrorMessage(`${I18n.t("Failed to get field with fieldName", { fieldName: field.fieldName })}`);
          }
        })
      );

      if (!newFields.length) {
        return;
      }

      updatePage({
        fields: [...(cachedData?.fields || []), ...newFields],
      });
    },
    [cachedData, updatePage, showErrorMessage, getFhq4QuestionnaireField, combinedItems]
  );

  const handleFieldDelete = React.useCallback((field: FieldInList) => {
    updatePage({
      fields: cachedData?.fields
        ?.filter((x) => 
          getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) !== getUniqueFieldCombination({ id: field.original.progenyId, context: field.original.context, type: field.original.progenyFieldType })
        )
        .sort((a, b) => a.order - b.order)
        .map((f, i) => ({
          ...f,
          order: i + 1,
        }))
    });
  }, [cachedData, updatePage]);

  const handleFieldChange = React.useCallback((field: QuestionnaireFieldUpdateDto | QuestionnaireSubfieldUpdateDto | QuestionnaireFieldAddDto) => {
    setEditingField(field);
    updatePage({
      fields: cachedData?.fields?.map((x) => 
        getUniqueFieldCombination({ id: x.progenyId, context: x.context, type: x.progenyFieldType }) === getUniqueFieldCombination({ id: field.progenyId, context: field.context, type: field.progenyFieldType }) 
          ? field 
          : x
      ),
    });
  }, [cachedData, updatePage]);


  const goBackFromFieldEdit = React.useCallback(() => {
    setEditingFieldUniqueCombination(undefined);
    setEditingField(undefined);
  }, []);

  const isFirstNameField = React.useCallback((field?: QuestionnaireFieldAddDto | QuestionnaireFieldUpdateDto) => {
    if (!questionnaire.homeTab.firstNameFieldId || !field) {
      return false;
    }

    const result =
      getUniqueFieldCombination({
        id: questionnaire.homeTab.firstNameFieldId,
        system: questionnaire.homeTab.firstNameFieldSystem,
        context: FIRST_NAME_ALWAYS_HAS_INDIVIDUAL_CONTEXT,
      }) ===
      getUniqueFieldCombination({
        id: field.progenyId,
        system: field.systemField,
        context: field.context,
      });

    return result;
  }, [questionnaire.homeTab.firstNameFieldId, questionnaire.homeTab.firstNameFieldSystem]);

  const canChangeFieldOrder = React.useCallback((field: FieldInList) => {
    return !isFirstNameField(field.original);
  }, [isFirstNameField]);

  const canDeleteField = React.useCallback((field: FieldInList) => {
    return !isFirstNameField(field.original);
  }, [isFirstNameField]);

  const canShowHideField = React.useCallback(() => {
    return false;
  }, []);

  const fieldHasError = React.useCallback((field: FieldInList) => {
    return errors?.fields ? Boolean(errors?.fields[field.original.progenyId]) : false;
  }, [errors]);

  const handleAddTextBlock = React.useCallback(() => {
    addTextBlock({ startOrder: combinedItems.length });
  }, [addTextBlock, combinedItems]);


  const handleFieldsOrTextBlocksChange = React.useCallback((params: {
    texts: UpdatedLocalTexts<QuestionnaireTextBlockLocalDto>;
    fields: UpdatedLocalFields<FieldInList>;
}) => {
    updatePage({
      texts: params.texts,
      fields: params.fields.map(({order, ...field}) => (
          {
            ...field.original,
            order,
            modified: field.original.modified || field.original.order != order
          }))
    })
  }, [updatePage]);

  return {
    handleTextBlockDelete,
    handleOrderChange,
    handleTextBlockChange,
    openTextBlockEditing,
    closeTextBlockEditing,
    editingTextBlock,
    addTextBlock,
    fieldHasError,
    canChangeFieldOrder,
    canDeleteField,
    isFirstNameField,
    processedFields,
    loadingPage,
    handleFieldChange,
    editingField,
    goBackFromFieldEdit,
    openFieldChooser,
    closeFieldChooser,
    fieldChooserOpened,
    handleFieldAdd,
    handlePageNameChange,
    handlePageDescriptionChange,
    handleFieldsOrderChange,
    handleFieldDelete,
    navigateOnFieldEdit,
    canShowHideField,
    handleAddTextBlock,
    combinedItems,
    handleFieldsOrTextBlocksChange
  };
};